import type { BarcodeAuditDto, BarcodeDto, ItemDetailDto, ItemDto, Paginated } from '@bnn/shared';
import { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../auth/AuthProvider';
import { BarcodeBindForm } from '../components/BarcodeBindForm';
import { fetchItemDetail, ItemPicker, UomOptions } from '../components/ItemPicker';
import { Alert, Badge, Button, Card, Empty, Field, Input, Modal, PageHeader, Pagination, Select, Table, useDebounced } from '../components/ui';
import { api, qs } from '../lib/api';
import { errMsg, useApi } from '../lib/useApi';

const SOURCE: Record<string, string> = { IMPORT: 'Import', MANUAL: 'เพิ่มเอง', SCAN: 'ผูกตอนนับ' };
const ACTION: Record<string, string> = { CREATE: 'สร้าง', UPDATE: 'แก้ไข', REMAP: 'ย้ายสินค้า', DEACTIVATE: 'ปิดใช้งาน', REACTIVATE: 'เปิดใช้งาน' };

export default function BarcodesPage() {
  const { can } = useAuth();
  const manage = can('barcodes.manage');
  const [params, setParams] = useSearchParams();
  const [search, setSearch] = useState(params.get('q') ?? '');
  const q = useDebounced(search.trim());
  const source = params.get('source') ?? '';
  const sessionId = params.get('session_id') ?? '';
  const active = params.get('active') ?? '';
  const [page, setPage] = useState(1);
  const list = useApi<Paginated<BarcodeDto>>(
    `/barcodes${qs({ search: q, source, session_id: sessionId, is_active: active, page, page_size: 50 })}`,
  );
  const [adding, setAdding] = useState(false);
  const [edit, setEdit] = useState<BarcodeDto | null>(null);
  const [history, setHistory] = useState<BarcodeDto | null>(null);
  const [notice, setNotice] = useState('');

  const set = (k: string, v: string) => {
    const next = new URLSearchParams(params);
    if (v) next.set(k, v);
    else next.delete(k);
    setParams(next, { replace: true });
    setPage(1);
  };

  return (
    <>
      <PageHeader
        title="Barcode"
        subtitle="1 สินค้า+หน่วย มีได้หลาย Barcode · 1 Barcode ชี้ได้สินค้า+หน่วยเดียวทั้งระบบ · ลบไม่ได้ ปิดใช้งานแทน"
        actions={
          manage && (
            <Button variant="primary" onClick={() => setAdding(true)}>
              + เพิ่ม Barcode
            </Button>
          )
        }
      />
      {notice && (
        <div className="mb-3">
          <Alert tone="ok">{notice}</Alert>
        </div>
      )}
      <Card>
        <div className="mb-3 flex flex-wrap gap-2">
          {[
            ['', 'ทั้งหมด'],
            ['SCAN', 'ผูกใหม่ตอนนับ'],
            ['IMPORT', 'Import'],
            ['MANUAL', 'เพิ่มเอง'],
          ].map(([v, l]) => (
            <Button key={v} size="sm" variant={source === v ? 'primary' : 'secondary'} onClick={() => set('source', v!)}>
              {l}
            </Button>
          ))}
          {sessionId && (
            <Badge tone="warn">
              เฉพาะรอบการนับที่เลือก{' '}
              <button className="ml-1 underline" onClick={() => set('session_id', '')}>
                ล้าง
              </button>
            </Badge>
          )}
        </div>
        <div className="mb-3 grid gap-2 sm:grid-cols-[1fr_180px]">
          <Input type="search" placeholder="ค้นหา Barcode / รหัส / ชื่อสินค้า / Supplier" value={search} onChange={(e) => setSearch(e.target.value)} />
          <Select value={active} onChange={(e) => set('active', e.target.value)} aria-label="สถานะ">
            <option value="">ทุกสถานะ</option>
            <option value="true">ใช้งาน</option>
            <option value="false">ปิดใช้งาน</option>
          </Select>
        </div>
        {list.error && <Alert>{list.error.message}</Alert>}
        {list.data && list.data.total === 0 ? (
          <Empty>ยังไม่มี Barcode{source === 'SCAN' ? 'ที่ผูกตอนนับ' : ''}</Empty>
        ) : (
          list.data && (
            <>
              <Table head={['Barcode', 'สินค้า', 'หน่วย', 'ที่มา', 'สถานะ', '']} minWidth={760}>
                {list.data.data.map((b) => (
                  <tr key={b.id} className={b.is_active ? '' : 'text-muted'}>
                    <td className="font-mono font-semibold whitespace-nowrap">{b.barcode}</td>
                    <td>
                      <Link to={`/items/${encodeURIComponent(b.item_code)}`} className="font-medium text-brand hover:underline">
                        {b.item_code}
                      </Link>{' '}
                      {b.item_name}
                      <div className="text-xs text-muted">
                        {b.barcode_type}
                        {b.supplier && ` · ${b.supplier}`}
                      </div>
                    </td>
                    <td className="whitespace-nowrap">{b.uom_code}</td>
                    <td className="text-xs">
                      {SOURCE[b.source]}
                      <div className="text-muted">
                        {b.created_by.display_name} · {new Date(b.created_at).toLocaleString('th-TH')}
                        {b.created_session_code && ` · ${b.created_session_code}`}
                      </div>
                    </td>
                    <td>{b.is_active ? <Badge tone="ok">ใช้งาน</Badge> : <Badge>ปิดใช้งาน</Badge>}</td>
                    <td className="space-x-1 text-right whitespace-nowrap">
                      <Button size="sm" onClick={() => setHistory(b)}>
                        ประวัติ
                      </Button>
                      {manage && (
                        <Button size="sm" onClick={() => setEdit(b)}>
                          จัดการ
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </Table>
              <Pagination page={list.data.page} pageSize={list.data.page_size} total={list.data.total} onPage={setPage} />
            </>
          )
        )}
      </Card>

      <Modal open={adding} title="เพิ่ม Barcode" onClose={() => setAdding(false)}>
        <BarcodeBindForm
          onDone={(b) => {
            setAdding(false);
            setNotice(`บันทึก Barcode ${b.barcode} → ${b.item_code} (${b.uom_code}) แล้ว`);
            list.reload();
          }}
          onCancel={() => setAdding(false)}
        />
      </Modal>
      <Modal open={!!edit} title={`จัดการ Barcode ${edit?.barcode ?? ''}`} onClose={() => setEdit(null)}>
        {edit && (
          <ManageBarcode
            b={edit}
            onDone={(msg) => {
              setEdit(null);
              setNotice(msg);
              list.reload();
            }}
          />
        )}
      </Modal>
      <Modal open={!!history} title={`ประวัติ ${history?.barcode ?? ''}`} onClose={() => setHistory(null)}>
        {history && <BarcodeHistory id={history.id} />}
      </Modal>
    </>
  );
}

function ManageBarcode({ b, onDone }: { b: BarcodeDto; onDone: (msg: string) => void }) {
  const [type, setType] = useState(b.barcode_type);
  const [supplier, setSupplier] = useState(b.supplier ?? '');
  const [reason, setReason] = useState('');
  const [remapItem, setRemapItem] = useState<ItemDetailDto | null>(null);
  const [remapUom, setRemapUom] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [affected, setAffected] = useState<{ session_code: string; entry_id: string; original_qty: string; uom_code: string }[] | null>(null);

  const run = async (fn: () => Promise<string | void>) => {
    setBusy(true);
    setError('');
    try {
      const msg = await fn();
      if (msg) onDone(msg);
    } catch (e) {
      setError(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-4">
      {error && <Alert>{error}</Alert>}
      <section className="space-y-2">
        <h4 className="font-semibold">แก้ไขรายละเอียด</h4>
        <div className="grid gap-2 sm:grid-cols-2">
          <Select value={type} onChange={(e) => setType(e.target.value as typeof type)}>
            <option value="FACTORY">FACTORY</option>
            <option value="INTERNAL">INTERNAL</option>
          </Select>
          <Input value={supplier} onChange={(e) => setSupplier(e.target.value)} placeholder="Supplier" />
        </div>
        <Button size="sm" loading={busy} onClick={() => void run(async () => (await api.patch(`/barcodes/${b.id}`, { barcode_type: type, supplier }), 'บันทึกการแก้ไขแล้ว'))}>
          บันทึก
        </Button>
      </section>

      <section className="space-y-2 border-t border-line pt-3">
        <h4 className="font-semibold">เหตุผล (บังคับสำหรับย้ายสินค้า / ปิดใช้งาน)</h4>
        <Input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="เช่น ผูกผิดสินค้า" />
      </section>

      <section className="space-y-2">
        <h4 className="font-semibold">ย้ายไปผูกสินค้าอื่น</h4>
        <p className="text-xs text-muted">ปัจจุบัน: {b.item_code} ({b.uom_code}) · รายการนับเดิมไม่เปลี่ยน (เก็บ Snapshot) ระบบจะแสดงรายการในรอบที่ยังเปิดอยู่ให้ตรวจ</p>
        {remapItem ? (
          <div className="space-y-2">
            <div className="rounded bg-chip px-3 py-2 text-sm">
              <b>{remapItem.item_code}</b> {remapItem.item_name}{' '}
              <button className="text-brand underline" onClick={() => setRemapItem(null)}>
                เปลี่ยน
              </button>
            </div>
            <Field label="หน่วย">
              <Select value={remapUom} onChange={(e) => setRemapUom(e.target.value)}>
                <UomOptions item={remapItem} />
              </Select>
            </Field>
            <Button
              size="sm"
              variant="danger"
              loading={busy}
              onClick={() =>
                void run(async () => {
                  const r = await api.post<{ affected_entries: { session_code: string; entry_id: string; original_qty: string; uom_code: string }[] }>(
                    `/barcodes/${b.id}/remap`,
                    { item_code: remapItem.item_code, uom_code: remapUom, reason },
                  );
                  if (r.affected_entries.length) setAffected(r.affected_entries);
                  else return `ย้าย Barcode ไป ${remapItem.item_code} (${remapUom}) แล้ว`;
                })
              }
            >
              ยืนยันย้าย
            </Button>
          </div>
        ) : (
          <ItemPicker
            autoFocus={false}
            placeholder="ค้นหาสินค้าปลายทาง"
            onPick={(i: ItemDto) =>
              void fetchItemDetail(i.item_code).then((d) => {
                setRemapItem(d);
                setRemapUom(d.inventory_uom);
              })
            }
          />
        )}
        {affected && (
          <Alert tone="warn">
            ย้ายแล้ว · มี {affected.length} รายการนับในรอบที่ยังเปิดอยู่ที่ใช้ Barcode นี้ — ให้ Void และนับใหม่ในหน้ารอบการนับ:
            <ul className="mt-1 list-disc pl-5">
              {affected.slice(0, 10).map((a) => (
                <li key={a.entry_id}>
                  {a.session_code}: {a.original_qty} {a.uom_code}
                </li>
              ))}
            </ul>
          </Alert>
        )}
      </section>

      <section className="border-t border-line pt-3">
        {b.is_active ? (
          <Button variant="danger" loading={busy} onClick={() => void run(async () => (await api.post(`/barcodes/${b.id}/deactivate`, { reason }), 'ปิดใช้งาน Barcode แล้ว'))}>
            ปิดใช้งาน Barcode
          </Button>
        ) : (
          <Button loading={busy} onClick={() => void run(async () => (await api.post(`/barcodes/${b.id}/reactivate`, reason ? { reason } : {}), 'เปิดใช้งาน Barcode แล้ว'))}>
            เปิดใช้งาน Barcode
          </Button>
        )}
      </section>
    </div>
  );
}

function BarcodeHistory({ id }: { id: string }) {
  const { data, error } = useApi<BarcodeAuditDto[]>(`/barcodes/${id}/audit`);
  if (error) return <Alert>{error.message}</Alert>;
  const show = (v: unknown) => {
    if (!v || typeof v !== 'object') return '';
    const o = v as Record<string, unknown>;
    return [o.item_code, o.uom_code, o.barcode_type, o.supplier, o.is_active === false ? 'ปิดใช้งาน' : null].filter(Boolean).join(' · ');
  };
  return (
    <ul className="divide-y divide-line">
      {data?.map((a) => (
        <li key={a.id} className="py-2 text-sm">
          <div>
            <b>{ACTION[a.action] ?? a.action}</b> · {a.user?.display_name} · {new Date(a.created_at).toLocaleString('th-TH')}
          </div>
          {a.old_value != null && <div className="text-muted">เดิม: {show(a.old_value)}</div>}
          <div className="text-muted">ใหม่: {show(a.new_value)}</div>
          {a.reason && <div>เหตุผล: {a.reason}</div>}
        </li>
      ))}
    </ul>
  );
}
